// Buddha KA + KAfrequency — canonical content.
// All facts unknown to us stay as visible editable placeholders.
// Never fabricate release dates, prices, streaming URLs, quotes, or stats.

import innerFlameCover from "@/assets/the-inner-flame-sutra-cover.png.asset.json";
import siddhaSutraCover from "@/assets/the-siddha-sutra-cover.jpeg.asset.json";
import ascendedAndArmedCover from "@/assets/ascended-and-armed-cover.png.asset.json";
import rebirthSutraCover from "@/assets/the-rebirth-sutra-cover.png.asset.json";
import diamondSutraCover from "@/assets/the-diamond-sutra-cover.png.asset.json";
import goldenLotusPreview from "@/assets/golden-lotus-preview.wav.asset.json";
import fortySevenGatesCover from "@/assets/47-gates-of-consciousness-cover.png.asset.json";
import crystallizedMindCover from "@/assets/the-crystallized-mind-cover.png.asset.json";

export type StreamingPlatform =
  | "Spotify" | "Apple Music" | "YouTube" | "SoundCloud"
  | "TikTok" | "Instagram" | "Facebook" | "Bandcamp" | "Discord"
  | "Audiomack" | "SoundClick";

export const PLATFORMS: StreamingPlatform[] = [
  "Spotify", "Apple Music", "YouTube", "SoundCloud",
  "TikTok", "Instagram", "Facebook",
];

// Public streaming buttons are disabled until real URLs are supplied.
// Editors adding a URL upgrade the button to an active link.
export const STREAMING_LINKS: Partial<Record<StreamingPlatform, string>> = {
  Spotify: "https://open.spotify.com/artist/56WjH3kUoYr8nyRqbpLudu",
  "Apple Music": "https://music.apple.com/us/artist/buddha-ka/1789693044",
  YouTube: "https://youtube.com/@buddhaka",
  SoundCloud: "https://soundcloud.com/buddha_ka",
  Instagram: "https://www.instagram.com/buddhaka9_",
  TikTok: "https://www.tiktok.com/@buddha_ka",
  Facebook: "https://www.facebook.com/share/1EEj1CCvW7/",
  // Canonical Audiomack account. A stale duplicate account exists in public
  // search; only this handle may ever be emitted by the site.
  Audiomack: "https://audiomack.com/buddha_ka",
  SoundClick: "https://www.soundclick.com/BuddhaKA",
};

/**
 * Canonical display order for the official Buddha KA artist-level profiles.
 * Single source for the /artist/buddha-ka profile grid and any sameAs output.
 */
export const ARTIST_PROFILE_ORDER: StreamingPlatform[] = [
  "Spotify",
  "Apple Music",
  "YouTube",
  "SoundCloud",
  "Audiomack",
  "SoundClick",
  "Instagram",
  "TikTok",
  "Facebook",
];

/** The official profile URLs, in canonical display order. */
export const OFFICIAL_ARTIST_PROFILES: Array<{ platform: StreamingPlatform; url: string }> =
  ARTIST_PROFILE_ORDER.map((platform) => ({ platform, url: STREAMING_LINKS[platform] })).filter(
    (p): p is { platform: StreamingPlatform; url: string } => Boolean(p.url),
  );

export interface ReleaseCredit {
  role: string;
  name: string;
}

export interface TrackListing {
  title: string;
  duration?: string; // e.g. "3:42"
  slug?: string;     // when present, track has its own hub at /music/$slug/song/$song
  audioUrl?: string; // full preview/stream URL when available
  lyrics?: string[]; // lyric lines
  credits?: ReleaseCredit[];
  links?: Partial<Record<StreamingPlatform, string>>;
  meaning?: string;          // one-paragraph decoded meaning of the song
  story?: string;            // behind-the-scenes narrative
  hiddenSymbols?: { symbol: string; meaning: string }[]; // esoteric layer
  productionNotes?: string[]; // BPM, key, sample sources, mix philosophy
  gallery?: { url: string; caption?: string }[]; // visual imagery for the song
  wallpapers?: { url: string; label?: string; resolution?: string }[]; // downloadable wallpapers
}

export interface Release {
  slug: string;
  title: string;
  type: "Album" | "EP" | "Single" | "Mixtape";
  year?: number;               // known release year
  releaseDate?: string;        // ISO date "YYYY-MM-DD" when known
  runtime?: string;            // e.g. "42 min"
  statement: string;
  tracks?: TrackListing[];     // omit until a real tracklist is supplied
  mood: string[];
  frequency: string;
  status: "released" | "upcoming" | "in-progress";
  coverUrl?: string;           // CDN URL for cover art
  purchaseUrl?: string;        // direct buy / iTunes / Apple Music album link
  purchaseLabel?: string;      // label for the purchase CTA
  links?: Partial<Record<StreamingPlatform, string>>;
  /** Distributor pre-save destination (e.g. DistroKid HyperFollow). Pre-release only. */
  preSaveUrl?: string;
  previewUrl?: string;         // CDN URL for audio preview clip
  credits?: ReleaseCredit[];
  lyricsExcerpts?: string[];
}

/** Find a song across releases by its slug. Returns null when unknown. */
export function findSong(releaseSlug: string, songSlug: string):
  | { release: Release; track: TrackListing; index: number }
  | null {
  const release = RELEASES.find((r) => r.slug === releaseSlug);
  if (!release?.tracks) return null;
  const index = release.tracks.findIndex((t) => t.slug === songSlug);
  if (index < 0) return null;
  return { release, track: release.tracks[index], index };
}

/**
 * Canonical fallback for the latest live release when no dated release data
 * is available. Every "current release" surface resolves through
 * {@link getCurrentRelease}, which prefers real release dates.
 */
export const CURRENT_RELEASE_SLUG = "the-diamond-sutra";

// Featured slug drives the Featured Release module — always the current release.
export const FEATURED_RELEASE_SLUG = CURRENT_RELEASE_SLUG;

/** Release-date cutover timezone. Public state flips at local midnight here. */
export const RELEASE_TIMEZONE = "America/Chicago";

/** Today's date as "YYYY-MM-DD" in the canonical release timezone. */
export function releaseToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: RELEASE_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

export type ReleaseState = "released" | "upcoming" | "in-progress";

/**
 * SINGLE SOURCE OF TRUTH for public release state.
 *
 * A dated release is authoritative: on or after its release date (local
 * midnight in {@link RELEASE_TIMEZONE}) it is live, before it is upcoming.
 * This removes any manual launch-day code edit. Undated releases fall back to
 * their editorial status, except that a past year can never read as upcoming.
 */
export function resolveReleaseState(
  r: { status?: string | null; releaseDate?: string | null; year?: number | null },
  now: Date = new Date(),
): ReleaseState {
  const declared: ReleaseState =
    r.status === "released" || r.status === "upcoming" || r.status === "in-progress"
      ? r.status
      : "in-progress";

  if (r.releaseDate && /^\d{4}-\d{2}-\d{2}$/.test(r.releaseDate)) {
    return r.releaseDate <= releaseToday(now) ? "released" : "upcoming";
  }

  const currentYear = Number(releaseToday(now).slice(0, 4));
  if (r.year && r.year < currentYear && declared === "upcoming") return "released";
  return declared;
}

/** The next scheduled (not yet released) release, earliest date first. */
export function getNextRelease<T extends { releaseDate?: string; status?: string; year?: number }>(
  releases: T[],
  now: Date = new Date(),
): T | undefined {
  return releases
    .filter((r) => r.releaseDate && resolveReleaseState(r, now) === "upcoming")
    .sort((a, b) => (a.releaseDate! < b.releaseDate! ? -1 : 1))[0];
}

/**
 * Resolve the canonical current release: the most recently dated live release,
 * falling back to the canonical slug and then to any live release.
 */
export function getCurrentRelease<T extends { slug: string; status?: string; releaseDate?: string; year?: number }>(
  releases: T[],
  now: Date = new Date(),
): T | undefined {
  const live = releases.filter((r) => resolveReleaseState(r, now) === "released");
  const dated = live
    .filter((r) => r.releaseDate)
    .sort((a, b) => (a.releaseDate! > b.releaseDate! ? -1 : 1));
  return dated[0] ?? live.find((r) => r.slug === CURRENT_RELEASE_SLUG) ?? live[0];
}


/** Format a release date for public display. Returns null when unknown. */
export function formatReleaseDate(r: Pick<Release, "releaseDate" | "year">): string | null {
  if (r.releaseDate) {
    const d = new Date(r.releaseDate + "T00:00:00Z");
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
    }
  }
  if (r.year) return String(r.year);
  return null;
}

/** Short label — year only. Returns null when unknown. */
export function releaseYearLabel(r: Pick<Release, "year">): string | null {
  return r.year ? String(r.year) : null;
}

const RELEASES_DATA: Release[] = [
  {
    slug: "golden-lotus",
    title: "Golden Lotus",
    type: "Single",
    statement:
      "A single blooming from the gold field — a signature transmission for the disciplined heart. The lotus that remembers where its light came from.",
    mood: ["Devotional", "Regal"],
    frequency: "Featured",
    status: "in-progress",
    previewUrl: goldenLotusPreview.url,
  },
  {
    slug: "golden-frequency",
    title: "Golden Frequency",
    type: "Album",
    year: 2025,
    statement:
      "The opening transmission of the KAfrequency canon — sound engineered to reorder attention, restore posture, and awaken the interior architecture.",
    mood: ["Awakening", "Sovereign"],
    frequency: "Rising",
    status: "released",
  },
  {
    slug: "the-golden-wave",
    title: "The Golden Wave",
    type: "Album",
    year: 2025,
    // Owner-verified canonical release date.
    releaseDate: "2025-10-31",
    statement:
      "A tidal movement of vibration and instruction. Music as ceremony for those building freedom from the inside outward.",
    mood: ["Cinematic", "Devotional"],
    frequency: "Midday",
    status: "released",
  },
  {
    slug: "ascended-and-armed",
    title: "Ascended and Armed",
    type: "Album",
    year: 2026,
    // Owner-verified canonical release date.
    releaseDate: "2026-03-27",
    statement:
      "Peace with structure. Softness with sovereignty. A body of work for the disciplined heart in a loud world.",
    mood: ["Warrior", "Grounded"],
    frequency: "Creative",
    status: "released",
    coverUrl: ascendedAndArmedCover.url,
  },
  {
    slug: "the-golden-ka",
    title: "The Golden KA",
    type: "Album",
    statement:
      "The signature of the double — the inner self remembered in gold. Layered production written to be replayed for years, not consumed in a scroll.",
    mood: ["Sacred", "Regal"],
    frequency: "Evening",
    status: "in-progress",
  },
  {
    slug: "the-siddha-sutra",
    title: "The Siddha Sutra",
    type: "Album",
    year: 2026,
    // Owner-verified canonical release date.
    releaseDate: "2026-06-21",
    statement:
      "Verses for the accomplished one. Music that carries instruction without becoming a lecture — the sutra spoken in rhythm.",
    mood: ["Contemplative", "Mystical"],
    frequency: "Night",
    status: "released",
    coverUrl: siddhaSutraCover.url,
    links: {
      // Owner-verified canonical release URLs — no tracking/share parameters.
      Spotify: "https://open.spotify.com/album/7uRzBQG7oCtags13Wem5oA",
      "Apple Music": "https://music.apple.com/us/album/the-siddha-sutra/6774707923",
      YouTube: "https://www.youtube.com/playlist?list=OLAK5uy_l4LH1ouGeqdl0Wikyy1uSbfE-PnGO9Fj8",
    },
  },
  {
    slug: "the-rebirth-sutra",
    title: "The Rebirth Sutra",
    type: "Album",
    year: 2026,
    // Owner-verified canonical release date.
    releaseDate: "2026-04-12",
    statement:
      "How gods are born. A sutra for the threshold between the old self and the one that walks out of the fire remembering everything.",
    mood: ["Sacred", "Mystical", "Regal"],
    frequency: "Night",
    status: "released",
    coverUrl: rebirthSutraCover.url,
  },
  {
    slug: "the-inner-flame-sutra",
    title: "The Inner Flame Sutra",
    type: "Album",
    year: 2026,
    // Owner-verified canonical release date.
    releaseDate: "2026-05-15",
    statement:
      "The interior fire, spoken as sutra. A record for the disciple who learns by heat — devotion that does not burn out because it is already the flame.",
    mood: ["Devotional", "Warrior", "Sacred"],
    frequency: "Rising",
    status: "released",
    coverUrl: innerFlameCover.url,
    purchaseUrl: "https://music.apple.com/us/album/the-inner-flame-sutra/6767411205",
    purchaseLabel: "Buy on Apple Music",
    links: {
      // Owner-verified canonical release URLs — no tracking/share parameters.
      Spotify: "https://open.spotify.com/album/5PbmLPpUsTsxTcr9AFbnHP",
      "Apple Music": "https://music.apple.com/us/album/the-inner-flame-sutra/6767411205",
      YouTube: "https://www.youtube.com/playlist?list=OLAK5uy_lQ6scZH0jB2Sv3z9fFj94sdq_ImsAcpoU",
    },
  },
  {
    // Released August 19, 2026. Credits and streaming URLs are added when the
    // owner supplies them — never invented here.
    slug: "the-diamond-sutra",
    title: "The Diamond Sutra",
    type: "Album",
    year: 2026,
    releaseDate: "2026-08-19",
    statement:
      "The indestructible mind, cut clean. A sutra written for the practitioner who has stopped negotiating with illusion — sound shaped like a blade that only severs what was never real.",
    mood: ["Sacred", "Warrior", "Regal"],
    frequency: "Rising",
    status: "released",
    coverUrl: diamondSutraCover.url,
    tracks: [
      { title: "Collect My Thoughts", slug: "collect-my-thoughts" },
      { title: "The Pen Breathes", slug: "the-pen-breathes" },
      { title: "Letter From the Higher Self", slug: "letter-from-the-higher-self" },
      { title: "Break the Frame", slug: "break-the-frame" },
      { title: "Borrowed Eyes", slug: "borrowed-eyes" },
      { title: "Borrowed Minds", slug: "borrowed-minds" },
      { title: "Who Taught You to Want?", slug: "who-taught-you-to-want" },
      { title: "Before the Bloom", slug: "before-the-bloom" },
      { title: "Spiritual Mechanics", slug: "spiritual-mechanics" },
      { title: "Tunnel View", slug: "tunnel-view" },
      { title: "Diamond", slug: "diamond" },
      { title: "Speak Life", slug: "speak-life" },
      { title: "Create", slug: "create" },
      { title: "Unleash Divine", slug: "unleash-divine" },
      { title: "Field of Dreams", slug: "field-of-dreams" },
      { title: "Elevate", slug: "elevate" },
      { title: "Different Math", slug: "different-math" },
    ],
  },
];

/**
 * Public release catalog. Editorial status is always re-derived through
 * {@link resolveReleaseState} so a dated release flips to live on its own
 * release day with no code edit, and no past-dated release ever reads as
 * "Upcoming" or "In progress".
 */
export const RELEASES: Release[] = RELEASES_DATA.map((r) => ({
  ...r,
  status: resolveReleaseState(r),
}));


/**
 * The announced next release — drives the sitewide pre-release funnel.
 * `null` while nothing further is announced: The Diamond Sutra released
 * August 19, 2026 and no later date has been confirmed by the owner.
 */
export const NEXT_RELEASE_SLUG: string | null = null;



export interface Activation {
  slug: string;
  title: string;
  category: string;
  timeLabel: string;
  duration: string;
  breathPattern: "box" | "coherent" | "478";
  intention: string;
  instruction: string;
  phrase: string;
  journal: string;
}

export const ACTIVATION_CATEGORIES = [
  "Rising Activation", "Midday Activation", "Evening Activation", "Night Seal",
  "Attention Discipline", "Breath Command", "Inner Infrastructure",
  "Field Governance", "Nervous System Freedom", "Energetic Ownership",
  "Creative Activation", "Protection", "Prosperity and Sacred Work",
];

export const ACTIVATIONS: Activation[] = [
  {
    slug: "rising-activation",
    title: "Rising Activation",
    category: "Rising Activation",
    timeLabel: "5:00 AM",
    duration: "7 minutes",
    breathPattern: "box",
    intention:
      "Meet the day before the day meets you. Return authorship to the interior before external inputs arrive.",
    instruction:
      "Sit upright. Palms upturned on the thighs. Let the spine remember what the mind has forgotten. Breathe the pattern below without commentary. When thought arrives, let it pass without ownership.",
    phrase: "I do not begin the day. I return to it.",
    journal: "What am I authoring today — and what am I refusing to inherit?",
  },
  {
    slug: "midday-activation",
    title: "Midday Activation",
    category: "Midday Activation",
    timeLabel: "11:00 AM",
    duration: "4 minutes",
    breathPattern: "coherent",
    intention:
      "Interrupt the drift. Sever the residue of the morning's noise. Reclaim the field before it is spent unconsciously.",
    instruction:
      "Stand or sit. Close the mouth. Breathe evenly through the nose. Withdraw attention from every open thread. Return only to the breath and the room you are standing in.",
    phrase: "The field is mine again.",
    journal: "Where have I leaked attention this morning that I intend to seal?",
  },
  {
    slug: "evening-activation",
    title: "Evening Activation",
    category: "Evening Activation",
    timeLabel: "5:00 PM",
    duration: "9 minutes",
    breathPattern: "coherent",
    intention:
      "Metabolize the day. Complete what the mind has left open. Prepare the interior for stillness.",
    instruction:
      "Sit low. Loosen the jaw and shoulders. Breathe long, low, and even. Let each cycle carry one unfinished thing to completion — not to solution.",
    phrase: "The day is complete in me.",
    journal: "What did I encounter today that deserves to be metabolized, not repeated?",
  },
  {
    slug: "night-seal",
    title: "Night Seal",
    category: "Night Seal",
    timeLabel: "7:47 PM",
    duration: "6 minutes",
    breathPattern: "478",
    intention:
      "Seal the field before sleep. Nothing that entered during the day is permitted to inherit the night.",
    instruction:
      "Lie down or sit reclined. Breathe the 4-7-8 pattern below. With each exhale, release ownership of one impression that is not yours to carry into dream.",
    phrase: "I sleep as sovereign, not as inheritor.",
    journal: "What am I refusing to sleep with tonight?",
  },
  {
    slug: "attention-discipline",
    title: "Attention Discipline",
    category: "Attention Discipline",
    timeLabel: "Anytime",
    duration: "5 minutes",
    breathPattern: "box",
    intention:
      "Attention is sacred currency. Practice spending it deliberately — not leaking it into rooms that did not ask permission to enter yours.",
    instruction:
      "Choose a single object in the room. Rest your gaze there. Every time attention departs, return it without punishment. This is not concentration. This is authorship over spending.",
    phrase: "What I attend to, I become.",
    journal: "Where is my attention currently spent — and is the return worth the cost?",
  },
  {
    slug: "breath-command",
    title: "Breath Command",
    category: "Breath Command",
    timeLabel: "Before hard work",
    duration: "6 minutes",
    breathPattern: "box",
    intention:
      "The breath is the first instrument of governance. When it is ordered, the nervous system remembers who is in charge.",
    instruction:
      "Sit tall. Breathe the box pattern below with even measure. Do not chase the count — inhabit it. Command is not force. It is rhythm the body agrees to.",
    phrase: "The breath is the first law I obey.",
    journal: "Where in my life have I outsourced command that only my breath should carry?",
  },
  {
    slug: "field-governance",
    title: "Field Governance",
    category: "Field Governance",
    timeLabel: "Before meetings, calls, public rooms",
    duration: "5 minutes",
    breathPattern: "coherent",
    intention:
      "Before you enter any room, govern the field you are bringing into it. Everything you have not sealed will enter with you.",
    instruction:
      "Stand. Breathe coherent 5-5. Name silently the three impressions you refuse to carry across the threshold. Cross once they are named and released.",
    phrase: "I do not walk in as a fragment. I walk in as a field.",
    journal: "What am I about to enter — and who arrives with me if I do not govern first?",
  },
  {
    slug: "nervous-system-freedom",
    title: "Nervous System Freedom",
    category: "Nervous System Freedom",
    timeLabel: "After difficulty",
    duration: "8 minutes",
    breathPattern: "478",
    intention:
      "Freedom is not the absence of stimulus. It is the nervous system that no longer confuses stimulus with instruction.",
    instruction:
      "Sit or lie reclined. Breathe 4-7-8. Let the long exhale teach the body that safety is available now — even where the mind is still catching up.",
    phrase: "I am not what happened to me. I am the field returning.",
    journal: "What is my nervous system still treating as urgent that is no longer real?",
  },
  {
    slug: "inner-infrastructure",
    title: "Inner Infrastructure",
    category: "Inner Infrastructure",
    timeLabel: "Weekly · Sunday review",
    duration: "10 minutes",
    breathPattern: "coherent",
    intention:
      "Freedom without infrastructure collapses into drift. Build the interior systems that make sovereignty repeatable.",
    instruction:
      "Sit at a desk with paper. Breathe coherent 5-5 for six cycles. Then, without hurry, list the three systems your interior is currently missing — sleep, ledger, boundary, ritual, refusal. Choose one to reinforce this week.",
    phrase: "Peace is architecture, not accident.",
    journal: "Which system, if built, would make everything else easier?",
  },
  {
    slug: "creative-activation",
    title: "Creative Activation",
    category: "Creative Activation",
    timeLabel: "Before work",
    duration: "6 minutes",
    breathPattern: "coherent",
    intention:
      "Enter the studio, the page, the field, or the meeting as a maker — not a reactor.",
    instruction:
      "Breathe evenly. Visualize the finished thing as if it already exists in another timeline. Do not force the vision. Simply refuse to disbelieve it.",
    phrase: "The work exists. I am only walking toward it.",
    journal: "What am I making today that has been asking to be made for years?",
  },
];

export const BREATH_PATTERNS = {
  box: { label: "Box · 4-4-4-4", steps: [
    { phase: "Inhale", seconds: 4 },
    { phase: "Hold", seconds: 4 },
    { phase: "Exhale", seconds: 4 },
    { phase: "Hold", seconds: 4 },
  ]},
  coherent: { label: "Coherent · 5-5", steps: [
    { phase: "Inhale", seconds: 5 },
    { phase: "Exhale", seconds: 5 },
  ]},
  "478": { label: "4-7-8", steps: [
    { phase: "Inhale", seconds: 4 },
    { phase: "Hold", seconds: 7 },
    { phase: "Exhale", seconds: 8 },
  ]},
} as const;

export interface WisdomPost {
  slug: string;
  title: string;
  category: string;
  readingTime: string;
  excerpt: string;
  pullQuote: string;
  body: string[];
  relatedMusic?: string;      // release slug
  relatedActivation?: string; // activation slug
  relatedBook?: string;       // book slug
  /** Author-written topics. Editorial only — never derived from the prose.
   *  Maintained in the admin editor (wisdom_posts.tags). */
  tags?: string[];
}


export const WISDOM_CATEGORIES = [
  "Mirror Thoughts", "Essays", "Sacred Systems", "Inner Technology",
  "Creative Discipline", "Spiritual Mechanics", "Attention and Perception",
  "Samsara and Desire", "Energetic Ownership", "Sacred Business",
  "Behind the Music", "Artist Notes",
];

export const WISDOM: WisdomPost[] = [
  {
    slug: "attention-is-sacred-currency",
    title: "Attention Is Sacred Currency",
    category: "Attention and Perception",
    readingTime: "6 min",
    excerpt:
      "Every unmeasured moment of attention is a withdrawal from an account you did not know you were keeping.",
    pullQuote:
      "Attention is not what you notice. It is what you spend.",
    body: [
      "The modern condition is not distraction. It is untracked spending.",
      "You have been trained to believe that focus is a matter of willpower. It is not. Focus is a matter of accounting. What you do not track, the world spends for you.",
      "The first act of sovereignty is a ledger. Not of money. Of attention. Where it went, what it purchased, and whether the return was worth the price.",
      "Peace is not the absence of stimuli. Peace is the structure that governs which stimuli you consent to.",
      "Begin small. One waking hour a day, know exactly where your attention went. That single practice, held for a season, will restructure a life.",
    ],
    relatedActivation: "attention-discipline",
    relatedMusic: "the-golden-ka",
    relatedBook: "kafrequency-cognitive-manual",
  },
  {
    slug: "who-taught-the-want-to-want",
    title: "Who Taught the Want to Want?",
    category: "Samsara and Desire",
    readingTime: "7 min",
    excerpt:
      "Before you ask whether you can have what you want, ask who trained the wanting.",
    pullQuote:
      "A want you did not author is a debt you did not agree to carry.",
    body: [
      "Desire arrives dressed as identity. It says: this is who you are, this is what you love, this is what you must have to be complete.",
      "But most desire is inherited. It was taught by a screen, a scene, a wound, a father, a hunger that did not begin in you. You are not its origin — you are its distributor.",
      "The work is not to suppress desire. Suppression is only wanting in disguise. The work is to trace it.",
      "Sit with a want. Follow it upstream. Whose voice arrives when you defend it? Whose approval is quietly being sought? What ache is it standing on top of?",
      "When you find the origin, you do not have to renounce anything. You simply stop paying tribute to a want that was never yours to serve.",
    ],
    relatedActivation: "nervous-system-freedom",
    relatedMusic: "the-siddha-sutra",
    relatedBook: "the-crystallized-mind",
  },
  {
    slug: "peace-requires-structure",
    title: "Peace Requires Structure",
    category: "Sacred Systems",
    readingTime: "8 min",
    excerpt:
      "Peace is not a mood. It is an architecture. And architecture is deliberate.",
    pullQuote:
      "Softness without structure is only surrender in slower clothing.",
    body: [
      "There is a spiritual industry built around calm as feeling. That is not what the elders meant.",
      "Peace is the outcome of ordered interior systems: what you eat, what you consent to, what you refuse to inherit, what hour you sleep, what you allow near the ear before the sun rises.",
      "Systems are the choreography of freedom. Without them, freedom collapses into drift.",
      "The disciplined life is not the opposite of the free life. It is its only reliable delivery mechanism.",
      "Begin with one hour of the day that belongs entirely to you. Defend it like it is the door to the rest.",
    ],
    relatedActivation: "inner-infrastructure",
    relatedMusic: "ascended-and-armed",
    relatedBook: "47-gates-of-consciousness",
  },
  {
    slug: "the-inner-world-is-where-ownership-begins",
    title: "The Inner World Is Where Ownership Begins",
    category: "Energetic Ownership",
    readingTime: "5 min",
    excerpt:
      "You cannot own a house you do not first own the standing in.",
    pullQuote:
      "Ownership begins internally. Everything else is paperwork.",
    body: [
      "You will not out-earn a leaking interior.",
      "The self must be occupied before it can be represented. The field must be governed before it can be shared.",
      "This is not withdrawal. This is preparation for a public life you can survive.",
    ],
    relatedActivation: "field-governance",
    relatedMusic: "the-golden-wave",
  },
  {
    slug: "music-as-instruction",
    title: "Music as Instruction",
    category: "Behind the Music",
    readingTime: "7 min",
    excerpt:
      "The best records do not tell you what to think. They rearrange the room you were thinking in.",
    pullQuote:
      "Art can carry instruction without becoming a lecture.",
    body: [
      "A record is not a message. It is a room.",
      "You enter it. It reorders you. You leave changed without being told what to change.",
      "That is the discipline of the KAfrequency canon. Instruction without imposition. Presence without performance.",
    ],
    relatedActivation: "creative-activation",
    relatedMusic: "the-golden-ka",
  },
];

/**
 * External/marketing book listing shown on `/books` and `/books/$slug`.
 * A `Book` represents the *cover, description, and outbound purchase link*
 * (Apple Books, Amazon, etc.) — NOT the readable text of the book.
 *
 * The full in-app readable edition lives in a different pair of tables:
 * `public.digital_titles` (kind='book') + `public.title_chapters`, which
 * power highlights, bookmarks, and reading progress in `/library/read/$slug`.
 *
 * These two are intentional and not duplicates: a book can exist as a
 * listing without a readable edition, and vice versa. When a book has both,
 * `books.digital_title_slug` bridges them and the public book page shows a
 * "Read the full edition in the Library" link.
 */
export interface Book {
  slug: string;
  title: string;
  subtitle: string;
  longDescription: string;
  /** Public author name for display. */
  author?: string;
  /** Legal / birth name, shown in parentheses when present. */
  authorLegalName?: string;
  /** Editorial category shown as eyebrow. */
  category?: string;
  /** Free-form tags used by search + recommendation graph. */
  tags?: string[];
  /** CDN URL for the book cover. */
  coverUrl?: string;
  /** Optional accessible alt text for the cover. Falls back to `${title} — cover art`. */
  coverAlt?: string;
  /** Off-site purchase URL (e.g. Apple Books, Amazon). When present the book
   *  is treated as an external product — no internal cart/checkout. */
  externalPurchaseUrl?: string;
  /** Label for the external purchase CTA. */
  externalPurchaseLabel?: string;
  /** Optional cover ratio hint. Defaults to portrait 3:4. */
  coverAspect?: "portrait" | "square";
  /** When true, promote in Featured Books modules. */
  featured?: boolean;
}

export const BOOKS: Book[] = [
  {
    slug: "47-gates-of-consciousness",
    title: "47 Gates of Consciousness",
    subtitle: "Forty-seven initiatory teachings inspired by the timeless wisdom of ancient Kemet.",
    longDescription:
      "47 Gates of Consciousness is a transformational guide inspired by the timeless wisdom of ancient Kemet. Through 47 initiatory teachings, readers explore consciousness, self-mastery, inner discipline, universal law, and spiritual development. The book serves as both a practical guide and a lifelong reference for cultivating greater awareness and purposeful living.",
    author: "Buddha KA",
    authorLegalName: "Curtis Shores",
    category: "Spiritual Wisdom · Consciousness",
    tags: [
      "book",
      "consciousness",
      "ancient kemet",
      "kemetic wisdom",
      "self-mastery",
      "inner discipline",
      "universal law",
      "spiritual development",
      "spiritual wisdom",
      "47 gates",
      "initiation",
      "awareness",
      "buddha ka",
      "curtis shores",
    ],
    coverUrl: fortySevenGatesCover.url,
    coverAlt:
      "47 Gates of Consciousness by Buddha KA (Curtis Shores), a transformational guide inspired by ancient Kemet exploring consciousness, self-mastery, universal law, and spiritual development.",
    externalPurchaseUrl: "https://books.apple.com/us/book/47-gate-of-consciousness/id6747275974",
    externalPurchaseLabel: "Read on Apple Books",
    featured: true,
  },
  {
    slug: "kafrequency-cognitive-manual",
    title: "KAfrequency Cognitive Manual",
    subtitle: "Instruction for the reordered mind.",
    longDescription:
      "A working manual for cognition as sovereign practice. Attention, perception, ownership, refusal, and the governance of the interior room.",
    author: "Buddha KA",
    category: "Inner Technology",
    tags: ["book", "cognition", "attention", "sovereignty"],
  },
  {
    slug: "the-crystallized-mind",
    title: "The Crystallized Mind",
    subtitle: "On stillness, structure, and sacred cognition.",
    longDescription:
      "The stilled mind is not empty. It is crystallized — ordered enough to reflect what enters without losing itself to it.",
    author: "Buddha KA",
    authorLegalName: "Curtis Shores",
    category: "Sacred Systems",
    coverUrl: crystallizedMindCover.url,
    coverAlt: "The Crystallized Mind — Awaken Your True Essence — official cover by Buddha KA (Curtis Shores)",
    tags: ["book", "stillness", "meditation", "structure"],
  },
];

// Primary navigation. Incomplete sections (Visuals, Books, Shop, Community)
// stay noindexed and accessible directly but are hidden from primary nav until
// they carry real, public-ready content.
export const NAV_LINKS = [
  { to: "/", label: "The First Gate" },
  { to: "/method", label: "The Method" },
  { to: "/gates", label: "The 47 Gates" },
  { to: "/music", label: "Music" },
  { to: "/beats", label: "Beats" },
  { to: "/activations", label: "Activations" },
  { to: "/intelligence", label: "KA Intelligence" },
  { to: "/library", label: "The Living Library" },
  { to: "/wisdom", label: "Wisdom" },
  { to: "/books", label: "Books" },
  { to: "/constellation", label: "Constellation" },
  { to: "/shop", label: "Shop" },
  { to: "/events", label: "Events" },
  { to: "/academy", label: "Academy" },
  { to: "/journey", label: "Journey" },
  { to: "/about", label: "About" },
] as const;


export const MANIFESTO = [
  "Attention is sacred currency.",
  "Peace requires structure.",
  "The inner world is not an escape. It is where ownership begins.",
];

// Timeline for /about — only supported milestones carry a year.
const TIMELINE_BASE: Array<{ year: string; title: string; note: string; upcoming?: boolean }> = [
  { year: "2025", title: "Golden Frequency", note: "The opening transmission of the KAfrequency canon." },
  { year: "2025", title: "The Golden Wave", note: "Expansion of the KAfrequency field into tidal instruction." },
  { year: "2026", title: "Ascended and Armed", note: "Peace with structure — sovereignty for a loud world." },
  { year: "", title: "The Golden KA", note: "The signature of the double — inner self remembered in gold.", upcoming: true },
  { year: "", title: "The Siddha Sutra", note: "Verses for the accomplished one — sutra spoken in rhythm." },
  { year: "", title: "Books & Systems", note: "47 Gates, Cognitive Manual, and The Crystallized Mind released." },
];

/**
 * Timeline for /about. Year and upcoming state derive from the canonical
 * RELEASES source whenever the entry maps to a release, so a dated release can
 * never read as unresolved or upcoming here.
 */
export const TIMELINE: Array<{ year: string; title: string; note: string; upcoming?: boolean }> =
  TIMELINE_BASE.map((entry) => {
    const release = RELEASES.find((r) => r.title === entry.title);
    if (!release) return entry;
    const year =
      release.releaseDate?.slice(0, 4) ?? (release.year ? String(release.year) : entry.year);
    return {
      ...entry,
      year: release.status === "released" ? year : entry.year,
      upcoming: release.status === "released" ? undefined : entry.upcoming ?? true,
    };
  });
